import { useState } from 'react';
import { Button, Divider, Group, Modal, Stack, TextInput } from '@mantine/core';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { crmTypeLabels } from './crm-config';
import { PlatformConfigForm } from './platform-config-form';
import { defaultPlatformConfig, isPlatformConfigComplete, platformConfigForSave } from './platform-config';

export function PlatformCrmSetupModal({
  organization,
  onClose,
}: {
  organization: { id: string; name: string };
  onClose: () => void;
}) {
  const [name, setName] = useState(`${organization.name} CRM`);
  const [config, setConfig] = useState(defaultPlatformConfig);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const create = async () => {
    setError(null);
    setIsCreating(true);

    const { error: apiError } = await api.organizations({ organizationId: organization.id })['crm-integration'].post({
      name: name.trim(),
      type: 'platform',
      config: platformConfigForSave(config),
    });

    setIsCreating(false);

    if (apiError) {
      setError(getErrorMessage(apiError, 'Failed to create the CRM integration'));

      return;
    }

    onClose();
  };

  return (
    <Modal opened onClose={onClose} size="lg" title={`Set Up ${crmTypeLabels.platform} Integration`}>
      <Stack gap="md">
        <ErrorAlert message={error} />
        <TextInput label="Integration Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />

        <PlatformConfigForm config={config} onConfigChange={setConfig} />

        <Divider />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void create()}
            loading={isCreating}
            disabled={!name.trim() || !isPlatformConfigComplete(config)}
          >
            Create Integration
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
